import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { useAuth } from '@context/AuthContext'
import Button from '@components/common/Button'

const Login = () => {
  const { login } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      username: '',
      password: '',
    },
  })

  const onSubmit = async (data) => {
    setLoading(true)
    await login(data.username, data.password)
    setLoading(false)
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F5F7FA',
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 420,
          width: '100%',
          p: 4,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
        }}
      >
        {/* Logo */}
        <Box textAlign="center" mb={4}>
          <Box
            component="img"
            src="/images/uci-logo.png"
            alt="Uganda Cancer Institute"
            sx={{
              maxWidth: 220,
              height: 'auto',
              mb: 2,
            }}
          />
          <Typography variant="h5" fontWeight={700} color="text.primary">
            CMMS
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Computerized Maintenance Management System
          </Typography>
        </Box>

        {/* Login Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            {...register('username', { required: 'Username is required' })}
            fullWidth
            label="Username"
            error={!!errors.username}
            helperText={errors.username?.message}
            margin="normal"
            autoComplete="username"
            autoFocus
          />

          <TextField
            {...register('password', { required: 'Password is required' })}
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            error={!!errors.password}
            helperText={errors.password?.message}
            margin="normal"
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            loading={loading}
            sx={{ mt: 3, mb: 2, py: 1.5 }}
          >
            Sign In
          </Button>

          <Box textAlign="center">
            <Link
              to="/forgot-password"
              style={{
                color: '#1B5E7A',
                textDecoration: 'none',
                fontSize: '0.875rem',
              }}
            >
              Forgot your password?
            </Link>
          </Box>
        </form>

        <Box textAlign="center" mt={3}>
          <Typography
            component="a"
            href="https://uci.or.ug/"
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            sx={{
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': { color: '#1B5E7A', textDecoration: 'underline' },
            }}
          >
            Uganda Cancer Institute
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Computerized Maintenance Management System
          </Typography>
        </Box>
      </Paper>
    </Box>
  )
}

export default Login
